import { Component, ElementRef, HostBinding, HostListener, Input } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { NotifierService } from 'angular-notifier';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';

type Value = File | null;
type OnChangeFn = (file: Value) => void;
type OnTouchedFn = () => void;
const videoExtensions = [
  '.mp4',
  '.webm',
  '.ogg',
  '.mov',
  '.avi',
  '.wmv',
  '.flv',
  '.3gp',
  '.3g2',
  '.mpeg',
  '.mpg',
  '.mkv',
];

@Component({
  selector: 'app-form-file-upload',
  templateUrl: './form-fil-upload.component.html',
  styleUrls: ['./form-fil-upload.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: FormFilUploadComponent,
      multi: true
    }
  ]
})
export class FormFilUploadComponent implements ControlValueAccessor {

  @HostBinding('id')
  private hostId = 'file-upload-' + Math.random().toString(36).substring(2);

  public inputId = 'file-upload-input-' + Math.random().toString(36).substring(2);

  @Input()
  public accept = '';

  private url: string | ArrayBuffer | null | SafeUrl = '';

  @Input()
  set previewUrl(value: string | ArrayBuffer | null | SafeUrl) {
    this.url = value;
    this.contentType = this.getContentType(value);
  }

  get previewUrl(): string | ArrayBuffer | SafeUrl | null {
    return this.url;
  }

  public contentType: 'unknown' | 'video' | 'image' = 'unknown';

  public file: File | null = null;

  onChange: OnChangeFn = () => {};
  onTouched: OnTouchedFn = () => {};

  constructor(
    private readonly host: ElementRef<HTMLInputElement>,
    private readonly notifier: NotifierService,
    private readonly sanitizer: DomSanitizer,
  ) {}

  @HostListener('change', ['$event.target.files'])
  onChangeInput( event: FileList ): void {
    this.file = event && event.item(0);

    if (this.file) {
      const allowedTypes = this.accept.split(',').map(type => type.trim());
      const isTypeAllowed = allowedTypes.includes(this.file.type);
      const isTypeApplicationAllowed = allowedTypes.some((type) => {
        const application = type.split('/')[0];
        return this.file?.type.startsWith(application);
      });

      if (!isTypeAllowed && !isTypeApplicationAllowed) {
        this.file = null;
        this.notifier.notify('error', 'File type is not allowed');
        return;
      }
    }

    this.onChange(this.file);

    if (this.file) {
      if (this.file.type.startsWith('image')) {
        this.updatePreviewFromBlob(this.file);
      } else if (this.file.type.startsWith('application/pdf') || this.file.type.startsWith('video')) {
        this.previewUrl = '';
      }
    }
  }

  @HostListener('dropFiles', ['$event'])
  onDropFiles(event: FileList): void {
    this.onChangeInput(event);
  }

  writeValue(value: any): void {
    this.file = null;

    if (value instanceof File) {
      this.file = value;
      this.updatePreviewFromBlob(value);
      return;
    }

    if (typeof value === 'string') {
      this.previewUrl = value;
      return;
    }
  }

  setDisabledState(isDisabled: boolean): void {
    this.host.nativeElement.disabled = isDisabled;
  }

  registerOnChange(fn: OnChangeFn): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: OnTouchedFn): void {
    this.onTouched = fn;
  }

  private updatePreviewFromBlob(file: File): void {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => {
      if (typeof reader.result === 'string') {
        this.previewUrl = this.sanitizer.bypassSecurityTrustUrl(reader.result);
      } else {
        this.previewUrl = reader.result;
      }
    };
  }

  public getContentType(url: string | ArrayBuffer | null | SafeUrl | undefined): 'unknown' | 'video' | 'image' {
    if (!url) {
      return 'unknown';
    }

    if (typeof url === 'string') {
      const lowerUrl = url.toLowerCase();

      if (videoExtensions.some(ext => lowerUrl.endsWith(ext))) {
        return 'video';
      }
      return 'image';
    }

    return 'unknown';
  }

}
